import { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { 
  Search, Package, Truck, CheckCircle2, Clock, MapPin, 
  Calendar, ExternalLink, AlertCircle, Box
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Comandă Primită', icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500' },
  confirmed: { label: 'Comandă Confirmată', icon: CheckCircle2, color: 'text-blue-500', bg: 'bg-blue-500' },
  processing: { label: 'În Pregătire', icon: Box, color: 'text-purple-500', bg: 'bg-purple-500' },
  shipped: { label: 'Expediată', icon: Truck, color: 'text-cyan-500', bg: 'bg-cyan-500' },
  in_transit: { label: 'În Tranzit', icon: Truck, color: 'text-orange-500', bg: 'bg-orange-500' },
  delivered: { label: 'Livrată', icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-500' },
  cancelled: { label: 'Anulată', icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-500' },
};

const STATUS_ORDER = ['pending', 'confirmed', 'processing', 'shipped', 'in_transit', 'delivered'];

const OrderTrackingPage = () => {
  const [email, setEmail] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !orderNumber.trim()) {
      toast.error('Te rugăm să completezi toate câmpurile');
      return;
    }

    setLoading(true);
    setError('');
    setOrderData(null);

    try {
      const response = await axios.post(`${API}/track-order`, {
        email: email.trim().toLowerCase(),
        order_number: orderNumber.trim().toUpperCase()
      });
      setOrderData(response.data);
    } catch (err) {
      const message = err.response?.data?.detail || 'Comanda nu a fost găsită. Verifică datele introduse.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getTrackingUrl = (courier, awb) => {
    if (!courier || !awb) return null;
    const courierLower = courier.toLowerCase();
    if (courierLower.includes('fan')) {
      return `https://www.fancourier.ro/awb-tracking/?awb=${awb}`;
    } else if (courierLower.includes('sameday')) {
      return `https://sameday.ro/#awb=${awb}`;
    } else if (courierLower.includes('cargus')) {
      return `https://www.cargus.ro/tracking-romania/?t=${awb}`;
    }
    return null;
  };

  const getCurrentStatusIndex = (status) => {
    const idx = STATUS_ORDER.indexOf(status);
    return idx >= 0 ? idx : 0;
  };

  return (
    <>
      <Helmet>
        <title>Urmărește Comanda | DEPRO KICKS</title>
        <meta name="description" content="Urmărește statusul comenzii tale DEPRO KICKS. Introdu emailul și numărul comenzii pentru a vedea unde se află coletul tău." />
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a] py-12 md:py-20">
        <div className="px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight uppercase text-white mb-4" data-testid="tracking-title">
                URMĂREȘTE <span className="text-[#c00000]">COMANDA</span>
              </h1>
              <p className="font-body text-white/60 max-w-md mx-auto">
                Introdu adresa de email și numărul comenzii pentru a vedea statusul livrării
              </p>
            </div>

            {/* Search Form */}
            <motion.form
              onSubmit={handleSubmit}
              className="bg-[#121212] border border-white/5 p-6 md:p-8 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              data-testid="tracking-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                    Adresa de Email
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@exemplu.com"
                    className="bg-[#0a0a0a] border-white/10 text-white rounded-none h-12"
                    data-testid="tracking-email-input"
                  />
                </div>
                <div>
                  <label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">
                    Număr Comandă
                  </label>
                  <Input
                    type="text"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                    placeholder="DK-XXXXXX-XXXX"
                    className="bg-[#0a0a0a] border-white/10 text-white rounded-none h-12 uppercase"
                    data-testid="tracking-order-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#c00000] hover:bg-[#a00000] text-white rounded-none h-12 font-heading tracking-wider"
                data-testid="tracking-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    CAUTĂ COMANDA
                  </>
                )}
              </Button>
            </motion.form>

            {/* Error State */}
            {error && !orderData && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-500/10 border border-red-500/30 p-6 text-center"
                data-testid="tracking-error"
              >
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="font-body text-red-500">{error}</p>
              </motion.div>
            )}

            {/* Order Data */}
            {orderData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
                data-testid="tracking-result"
              >
                {/* Order Header */}
                <div className="bg-[#121212] border border-white/5 p-6">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                      <p className="font-body text-xs text-white/50 mb-1">NUMĂR COMANDĂ</p>
                      <p className="font-heading font-bold text-2xl text-[#c00000]" data-testid="tracking-order-number">
                        {orderData.order_number}
                      </p>
                    </div>
                    <div className={`px-4 py-2 ${STATUS_CONFIG[orderData.order_status]?.bg || 'bg-white/10'}`}>
                      <p className="font-heading text-sm text-white uppercase tracking-wider">
                        {STATUS_CONFIG[orderData.order_status]?.label || orderData.order_status}
                      </p>
                    </div>
                  </div>

                  {/* Progress Timeline */}
                  {orderData.order_status !== 'cancelled' && (
                    <div className="relative">
                      <div className="absolute top-4 left-0 right-0 h-1 bg-white/10" />
                      <div 
                        className="absolute top-4 left-0 h-1 bg-[#c00000] transition-all duration-500"
                        style={{ width: `${(getCurrentStatusIndex(orderData.order_status) / (STATUS_ORDER.length - 1)) * 100}%` }}
                      />
                      <div className="relative flex justify-between">
                        {STATUS_ORDER.slice(0, -1).map((status, idx) => {
                          const isActive = getCurrentStatusIndex(orderData.order_status) >= idx;
                          const isCurrent = orderData.order_status === status;
                          const StatusIcon = STATUS_CONFIG[status]?.icon || Clock;
                          return (
                            <div key={status} className="flex flex-col items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-colors ${
                                isActive ? 'bg-[#c00000]' : 'bg-[#1a1a1a] border border-white/20'
                              } ${isCurrent ? 'ring-4 ring-[#c00000]/30' : ''}`}>
                                <StatusIcon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-white/30'}`} />
                              </div>
                              <p className={`font-body text-[10px] mt-2 text-center max-w-[60px] ${
                                isActive ? 'text-white' : 'text-white/30'
                              }`}>
                                {STATUS_CONFIG[status]?.label.split(' ')[0]}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Shipping Info */}
                {(orderData.courier || orderData.awb) && (
                  <div className="bg-[#121212] border border-white/5 p-6">
                    <h3 className="font-heading text-sm text-white/50 mb-4 uppercase tracking-wider">
                      Informații Expediere
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {orderData.courier && (
                        <div className="bg-[#0a0a0a] p-4">
                          <Truck className="w-5 h-5 text-[#c00000] mb-2" />
                          <p className="font-body text-xs text-white/50">Curier</p>
                          <p className="font-heading text-white">{orderData.courier}</p>
                        </div>
                      )}
                      {orderData.awb && (
                        <div className="bg-[#0a0a0a] p-4">
                          <Package className="w-5 h-5 text-[#c00000] mb-2" />
                          <p className="font-body text-xs text-white/50">AWB / Tracking</p>
                          <p className="font-heading text-white">{orderData.awb}</p>
                        </div>
                      )}
                      {orderData.estimated_delivery && (
                        <div className="bg-[#0a0a0a] p-4">
                          <Calendar className="w-5 h-5 text-[#c00000] mb-2" />
                          <p className="font-body text-xs text-white/50">Livrare Estimată</p>
                          <p className="font-heading text-white">{orderData.estimated_delivery}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* External Tracking Link */}
                    {getTrackingUrl(orderData.courier, orderData.awb) && (
                      <a
                        href={getTrackingUrl(orderData.courier, orderData.awb)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 flex items-center justify-center gap-2 bg-[#c00000] hover:bg-[#a00000] text-white py-3 px-6 font-heading text-sm tracking-wider transition-colors"
                        data-testid="tracking-external-link"
                      >
                        <ExternalLink className="w-4 h-4" />
                        URMĂREȘTE PE SITE-UL CURIERULUI
                      </a>
                    )}
                  </div>
                )}

                {/* Status History */}
                {orderData.status_history && orderData.status_history.length > 0 && (
                  <div className="bg-[#121212] border border-white/5 p-6">
                    <h3 className="font-heading text-sm text-white/50 mb-4 uppercase tracking-wider">
                      Istoric Status
                    </h3>
                    <div className="space-y-4">
                      {[...orderData.status_history].reverse().map((entry, idx) => {
                        const StatusIcon = STATUS_CONFIG[entry.status]?.icon || Clock;
                        const statusColor = STATUS_CONFIG[entry.status]?.color || 'text-white/50';
                        return (
                          <div key={idx} className="flex gap-4">
                            <div className={`w-10 h-10 bg-[#0a0a0a] flex items-center justify-center flex-shrink-0`}>
                              <StatusIcon className={`w-5 h-5 ${statusColor}`} />
                            </div>
                            <div className="flex-1 border-b border-white/5 pb-4">
                              <p className={`font-heading text-sm ${statusColor}`}>
                                {entry.note || STATUS_CONFIG[entry.status]?.label || entry.status}
                              </p>
                              <p className="font-body text-xs text-white/30 mt-1">
                                {new Date(entry.timestamp).toLocaleString('ro-RO', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Order Items */}
                <div className="bg-[#121212] border border-white/5 p-6">
                  <h3 className="font-heading text-sm text-white/50 mb-4 uppercase tracking-wider">
                    Produse Comandate
                  </h3>
                  <div className="space-y-4">
                    {orderData.items?.map((item, idx) => (
                      <div key={idx} className="flex gap-4 bg-[#0a0a0a] p-4">
                        <img 
                          src={item.product_image} 
                          alt={item.product_name}
                          className="w-16 h-16 object-cover"
                        />
                        <div className="flex-1">
                          <p className="font-heading text-sm text-white">{item.product_name}</p>
                          <p className="font-body text-xs text-white/50">
                            Mărime: {item.size} | Cantitate: {item.quantity}
                          </p>
                        </div>
                        <p className="font-heading text-[#c00000]">{item.price} RON</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Total */}
                  <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
                    <span className="font-heading text-white">TOTAL</span>
                    <span className="font-heading text-2xl text-[#c00000]">{orderData.total?.toFixed(2)} RON</span>
                  </div>
                </div>

                {/* Delivery Location */}
                {orderData.address && (
                  <div className="bg-[#121212] border border-white/5 p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-5 h-5 text-[#c00000]" />
                      <h3 className="font-heading text-sm text-white/50 uppercase tracking-wider">
                        Locație Livrare
                      </h3>
                    </div>
                    <p className="font-body text-white">
                      {orderData.address.city}, {orderData.address.county}
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Help Section */}
            <div className="mt-12 text-center">
              <p className="font-body text-sm text-white/40">
                Ai întrebări despre comanda ta?{' '}
                <a href="/contact" className="text-[#c00000] hover:underline">
                  Contactează-ne
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default OrderTrackingPage;
